import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  startOfDay,
  endOfDay,
  subDays,
  addDays,
  subMonths,
  addMonths,
  parse,
  isValid,
  isSameDay,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { DateRange } from 'react-day-picker';

export type PeriodPreset = 
  | 'today'
  | 'this_week' 
  | 'this_month' 
  | 'this_year' 
  | 'last_30_days' 
  | 'last_12_months' 
  | 'all_time' 
  | 'custom';

interface PeriodSelectorProps {
  startDate?: Date;
  endDate?: Date;
  onPeriodChange: (startDate: Date | undefined, endDate: Date | undefined, preset?: PeriodPreset) => void;
  defaultPreset?: PeriodPreset;
}

const presetLabels: Record<PeriodPreset, string> = {
  today: 'Hoje',
  this_week: 'Esta semana',
  this_month: 'Este mês',
  this_year: 'Este ano',
  last_30_days: 'Últimos 30 dias',
  last_12_months: 'Últimos 12 meses',
  all_time: 'Todo o período',
  custom: 'Personalizado',
};

export function PeriodSelector({ 
  startDate, 
  endDate, 
  onPeriodChange,
  defaultPreset = 'this_month'
}: PeriodSelectorProps) {
  const [currentPreset, setCurrentPreset] = useState<PeriodPreset>(defaultPreset);
  const [customOpen, setCustomOpen] = useState(false);
  const [customRange, setCustomRange] = useState<DateRange | undefined>({
    from: startDate,
    to: endDate,
  });
  const [customStartInput, setCustomStartInput] = useState('');
  const [customEndInput, setCustomEndInput] = useState('');

  // Sincronizar inputs quando o popover abre
  useEffect(() => {
    if (customOpen) {
      if (customRange?.from) {
        setCustomStartInput(format(customRange.from, 'dd/MM/yyyy'));
      } else {
        setCustomStartInput('');
      }
      if (customRange?.to) {
        setCustomEndInput(format(customRange.to, 'dd/MM/yyyy'));
      } else {
        setCustomEndInput('');
      }
    }
  }, [customOpen, customRange?.from, customRange?.to]);

  // Quando digita no input, tenta parsear e atualizar o calendário
  const handleDateInputChange = (value: string, type: 'start' | 'end') => {
    if (type === 'start') {
      setCustomStartInput(value);
    } else {
      setCustomEndInput(value);
    }
    
    // Tenta parsear a data no formato dd/mm/yyyy
    const parsedDate = parse(value, 'dd/MM/yyyy', new Date());
    if (isValid(parsedDate) && value.length === 10) {
      setCustomRange(prev => ({
        from: type === 'start' ? parsedDate : prev?.from,
        to: type === 'end' ? parsedDate : prev?.to,
      }));
    }
  };

  // Quando seleciona no calendário, atualiza os inputs
  const handleRangeSelect = (range: DateRange | undefined) => {
    setCustomRange(range);
    if (range?.from) {
      setCustomStartInput(format(range.from, 'dd/MM/yyyy'));
    }
    if (range?.to) {
      setCustomEndInput(format(range.to, 'dd/MM/yyyy'));
    }
  };

  const handlePresetSelect = (preset: PeriodPreset) => {
    setCurrentPreset(preset);
    const now = new Date();

    switch (preset) {
      case 'today':
        onPeriodChange(startOfDay(now), endOfDay(now), preset);
        break;
      case 'this_week':
        onPeriodChange(startOfWeek(now, { weekStartsOn: 0 }), endOfWeek(now, { weekStartsOn: 0 }), preset);
        break;
      case 'this_month':
        onPeriodChange(startOfMonth(now), endOfMonth(now), preset);
        break;
      case 'this_year':
        onPeriodChange(startOfYear(now), endOfYear(now), preset);
        break;
      case 'last_30_days':
        onPeriodChange(subDays(now, 30), now, preset);
        break;
      case 'last_12_months':
        onPeriodChange(subMonths(now, 12), now, preset);
        break;
      case 'all_time':
        onPeriodChange(undefined, undefined, preset);
        break;
      case 'custom':
        setCustomOpen(true);
        break;
    }
  };

  // Verifica se o período atual é um único dia
  const isSingleDayPeriod = startDate && endDate && isSameDay(startDate, endDate);

  const handlePrevious = () => {
    const currentStart = startDate || new Date();
    
    if (isSingleDayPeriod) {
      // Navega dia a dia
      const newDate = subDays(currentStart, 1);
      setCurrentPreset('custom');
      onPeriodChange(startOfDay(newDate), endOfDay(newDate), 'custom');
    } else {
      // Navega mês a mês
      const newDate = subMonths(currentStart, 1);
      setCurrentPreset('custom');
      onPeriodChange(startOfMonth(newDate), endOfMonth(newDate), 'custom');
    }
  };

  const handleNext = () => {
    const currentStart = startDate || new Date();
    
    if (isSingleDayPeriod) {
      // Navega dia a dia
      const newDate = addDays(currentStart, 1);
      setCurrentPreset('custom');
      onPeriodChange(startOfDay(newDate), endOfDay(newDate), 'custom');
    } else {
      // Navega mês a mês
      const newDate = addMonths(currentStart, 1);
      setCurrentPreset('custom');
      onPeriodChange(startOfMonth(newDate), endOfMonth(newDate), 'custom');
    }
  };

  const handleCustomRangeApply = () => {
    if (customRange?.from) {
      setCurrentPreset('custom');
      onPeriodChange(customRange.from, customRange.to, 'custom');
      setCustomOpen(false);
    }
  };

  const getDisplayLabel = (): string => {
    if (currentPreset === 'all_time') {
      return 'Todo o período';
    }
    
    if (currentPreset === 'today') {
      return 'Hoje';
    }
    
    if (currentPreset !== 'custom') {
      return presetLabels[currentPreset];
    }

    if (startDate && endDate) {
      // Verifica se é um único dia
      if (isSameDay(startDate, endDate)) {
        return format(startDate, "dd 'de' MMMM", { locale: ptBR });
      }
      
      // Check if it's a full month
      const sameMonth = startDate.getMonth() === endDate.getMonth() && 
                        startDate.getFullYear() === endDate.getFullYear();
      if (sameMonth) {
        return format(startDate, "MMMM 'de' yyyy", { locale: ptBR });
      }
      return `${format(startDate, 'dd/MM/yy')} - ${format(endDate, 'dd/MM/yy')}`;
    }
    
    if (startDate) {
      return format(startDate, "MMMM 'de' yyyy", { locale: ptBR });
    }

    return 'Selecione um período';
  };

  return (
    <div className="flex items-center gap-1">
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={handlePrevious}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>

      {/* Container relativo para ancoragem correta do Popover */}
      <div className="relative">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="min-w-[180px] justify-between capitalize">
              {getDisplayLabel()}
              <ChevronDown className="h-4 w-4 ml-2 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[200px]">
            <DropdownMenuItem onClick={() => handlePresetSelect('today')}>
              Hoje
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handlePresetSelect('this_week')}>
              Esta semana
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handlePresetSelect('this_month')}>
              Este mês
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handlePresetSelect('this_year')}>
              Este ano
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handlePresetSelect('last_30_days')}>
              Últimos 30 dias
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handlePresetSelect('last_12_months')}>
              Últimos 12 meses
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handlePresetSelect('all_time')}>
              Todo o período
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handlePresetSelect('custom')}>
              Período personalizado...
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Popover ancorado ao container relativo */}
        <Popover open={customOpen} onOpenChange={setCustomOpen}>
          <PopoverTrigger asChild>
            <button 
              className="absolute inset-0 w-full h-full opacity-0 pointer-events-none" 
              aria-hidden="true"
              tabIndex={-1}
            />
          </PopoverTrigger>
          <PopoverContent 
            className="w-auto p-0 z-[9999] bg-popover" 
            align="start"
            side="bottom"
            sideOffset={4}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
            onPointerDownOutside={(e) => e.preventDefault()}
            onFocusOutside={(e) => e.preventDefault()}
          >
            <div className="p-4 space-y-4 pointer-events-auto">
              {/* Inputs manuais de data */}
              <div className="flex gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Data inicial</Label>
                  <Input
                    type="text"
                    placeholder="dd/mm/aaaa"
                    value={customStartInput}
                    onChange={(e) => handleDateInputChange(e.target.value, 'start')}
                    className="w-[120px]"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Data final</Label>
                  <Input
                    type="text"
                    placeholder="dd/mm/aaaa"
                    value={customEndInput}
                    onChange={(e) => handleDateInputChange(e.target.value, 'end')}
                    className="w-[120px]"
                  />
                </div>
              </div>
              
              {/* Calendário de range */}
              <Calendar
                mode="range"
                selected={customRange}
                onSelect={handleRangeSelect}
                numberOfMonths={2}
                locale={ptBR}
                className="pointer-events-auto"
              />
              
              {/* Botões de ação */}
              <div className="flex justify-end gap-2 pt-3 border-t">
                <Button variant="outline" size="sm" onClick={() => setCustomOpen(false)}>
                  Cancelar
                </Button>
                <Button size="sm" onClick={handleCustomRangeApply}>
                  Aplicar
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={handleNext}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}
